import React from 'react';
import { CheckIcon, StarIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
type Volume = {
  number: string;
  title: string;
  subtitle: string;
  body: string;
  promise: string;
  flag?: string;
};
const volumes: Volume[] = [{
  number: 'Volume 0',
  title: 'The Run of Show',
  subtitle: 'The Whole Day on One Timeline: Where You Are, What You’re Shooting, What’s Slipping',
  body: 'The master timeline of the wedding day with your position at every block, the shot targets per block, the four checkpoints that tell you whether the day is still on time, and the Break Points where a 15-minute delay quietly costs you golden hour. Includes the “there’s no planner, so I’m running the day” version.',
  promise: 'You always know what happens next — even when the schedule doesn’t.'
}, {
  number: 'Volume 1',
  title: 'Getting Ready',
  subtitle: 'Details, Small Rooms and Family Moments — Before Anyone Says “We’re Late”',
  body: 'The Detail Flat-Lay System (rings, gown, invitations — one table, one light), the Collection Sentence that gets every detail onto that table in one ask, the Claim-the-Window light plan for the “suite” that’s really a dark hotel room with one lamp, directed sets while hair is still happening, the parent moments (dad’s look, mom’s hands), and the Ask-Before-You-Arrive checklist.',
  promise: 'You walk into any room and know where the light is and what to shoot first.'
}, {
  number: 'Volume 2',
  title: 'First Look & Couple Prep',
  subtitle: 'Choreography for the Reveal — and the 25 Minutes After the Tears',
  body: 'The First Look Choreography (where each of them stands, where you stand, exactly what you say before you release them), the After-the-Reveal sequence for the minutes you usually have “nothing in your head” for, the No-First-Look Variants that keep your timeline math intact, and Wedding Party Portraits — including nine groomsmen and zero ideas.',
  promise: 'The reveal, the tears, and the twenty minutes after — all directed.'
}, {
  number: 'Volume 3',
  title: 'The Ceremony',
  subtitle: 'Positions, Restrictions and the Moments That Only Happen Once',
  body: 'Position maps by venue type (church, outdoor, ballroom, courthouse), the Restricted Ceremony playbook (no flash, no aisle, don’t move — here’s where you shoot the rings from), the non-repeatable moments sequence — entrance, vows, rings, kiss, exit — with your position for each, and unplugged-ceremony tactics for the fourteen iPads anyway.',
  promise: 'Never on the wrong side for the vows again.'
}, {
  number: 'Volume 4',
  title: 'Family & Group Formals',
  subtitle: 'The 12-Minute Formals Method',
  body: 'The Calling Order (smallest to largest, so nobody waits and nobody wanders), Call-by-Name direction so you never lose the group, Placement by Height, group diagrams by size, and the Fill-Before-the-Day Template the couple completes three weeks out — so you’re never handed 40 combinations five minutes before.',
  promise: 'Twelve minutes. Nobody yells. Nobody hates you.',
  flag: 'The heart of the system'
}, {
  number: 'Volume 5',
  title: 'Couple Portraits & Golden Hour',
  subtitle: 'The Progressions That Turn Stiff Couples Into Candid-Looking Frames',
  body: 'Pose Progressions (standing → movement → intimate) so you never run dry after five, the “just act natural” replacement — direction that produces movement, and movement produces the moment — the Flat Sky / Brick Wall / Twelve Minutes plan, rain variants, and Night Portraits for when dinner ate golden hour.',
  promise: 'Your mind doesn’t go blank, because the next line is already written.'
}, {
  number: 'Volume 6',
  title: 'The Reception',
  subtitle: 'Speeches, Dances, Cake, Dancefloor, Send-Off — and When You’re Allowed to Leave',
  body: 'The speeches light plan for the one orange spotlight from 1997, first dance and parent dance positions, the cake-cutting coordination line (so nobody moves it up without telling you), open-dancing coverage, grand exit choreography, and the “Don’t Leave Without This” list.',
  promise: 'Nothing gets missed because “nobody told you.”'
}];
const includes = ['Illustrated poses and position diagrams', 'Word-for-word direction lines — say them exactly, or in your own voice', 'Suggested minutes for every block', 'The 8-Minute Version — what to shoot when the schedule collapses', 'Break Points — what breaks if this part runs late, and what to cut first', 'The Printable — a one-page run sheet you clip to your bag', 'Universal — any country, any venue, any light'];
export function Tomes() {
  return <section className="w-full bg-ivory py-16 sm:py-24" data-id="element-318" data-mp="3t3ubom155ru">
      <div className="mx-auto max-w-6xl px-5" data-id="element-319" data-mp="2aiiesom7suum">
        <h2 className="text-center font-serif text-3xl sm:text-5xl text-ink" data-id="element-320" data-mp="2ajbbsxvdg5zk">
          Six volumes. One wedding day. Zero improvisation.
        </h2>
        <div className="rule-gold mx-auto mt-8 w-32" aria-hidden="true" data-id="element-321" data-mp="l1efevu1szp2" />

        <ul className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-322" data-mp="l3hh4d4kch9m">
          {volumes.map(volume => <li key={volume.number} className={`flex flex-col border bg-white p-7 transition-shadow duration-300 hover:shadow-card ${volume.flag ? 'border-gold lg:col-span-1' : 'border-gold-soft/70'}`} data-id="element-323" data-mp="pzog1lyjksi2">
              <div className="flex items-center justify-between gap-3" data-id="element-324" data-mp="2a3v4xitvl65">
                <span className="text-[10px] uppercase tracking-[0.24em] text-gold-dark" data-id="element-325" data-mp="6cqpb52z1i6i">
                  {volume.number}
                </span>
                {volume.flag && <span className="inline-flex items-center gap-1.5 bg-gold-tint px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-gold-dark" data-id="element-326" data-mp="3tgy4rjrllxyu">
                    <StarIcon className="h-3 w-3 fill-gold text-gold" aria-hidden="true" data-id="element-327" data-mp="1mr9scqmt5rnq" />
                    {volume.flag}
                  </span>}
              </div>

              <h3 className="mt-4 font-serif text-2xl leading-snug text-ink" data-id="element-328" data-mp="1q9fom54ni8f9">
                {volume.title}
              </h3>
              <p className="mt-2 text-xs uppercase tracking-[0.1em] text-ink-muted" data-id="element-329" data-mp="1lbnvx8w6mia0">
                {volume.subtitle}
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-relaxed text-ink-soft" data-id="element-330" data-mp="1bbx1163pm19e">
                {volume.body}
              </p>
              <p className="mt-6 border-t border-gold-soft/70 pt-5 font-serif text-base italic text-gold-dark" data-id="element-331" data-mp="28xuegp38fgeh">
                → {volume.promise}
              </p>
            </li>)}
        </ul>

        <div className="mt-12 border border-gold-soft bg-gold-tint px-6 py-8" data-id="element-332" data-mp="1z7rn0tbreai9">
          <p className="text-center text-[11px] uppercase tracking-[0.24em] text-ink-soft" data-id="element-333" data-mp="1u97zwplfisob">
            Inside every volume
          </p>
          <ul className="mt-5 flex flex-wrap items-center justify-center gap-x-7 gap-y-3" data-id="element-334" data-mp="2rrq6tu5cg73l">
            {includes.map(item => <li key={item} className="inline-flex items-center gap-2 text-sm text-ink" data-id="element-335" data-mp="2v7udpktrs440">
                <CheckIcon className="h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" data-id="element-336" data-mp="2z96966zbrjk4" />
                {item}
              </li>)}
          </ul>
        </div>

        <div className="mt-12 flex justify-center" data-id="element-337" data-mp="34ac2o70t7aup">
          <CtaButton data-id="element-338" data-mp="397lz46mm8ms1">I want the whole day scripted</CtaButton>
        </div>
      </div>
    </section>;
}